/**
 * householdPosition.ts — where each person lives, and what that means, said ONCE.
 *
 * The profile records a living arrangement per person and the means tests read it carefully:
 * homeowner or not, a village or granny flat interest above the extra allowable amount, a former
 * home kept while in care. But every screen used to answer "do they own a home?" its own way —
 * most read the CLIENT's arrangement alone, so a couple where only the partner owned the home lost
 * the property strategies, and Support at Home priced its contributions on the wrong thresholds.
 * (Owner, 7 Oct 2026: the living-arrangement permutations review.)
 *
 * Everything here is a predicate over the fact-find and the strategy options laid over it. No money
 * is assessed; these decide which rule the assessors and the screens then apply.
 */
import type { FactFind } from './factFindTypes.ts';
import type { StrategyOptions } from './strategyOptionsTypes.ts';
import { EXTRA_ALLOWABLE_AMOUNT } from './constants/ratesAndThresholds.ts';
import { isGrannyFlatResidency } from './grannyFlatArrangement.ts';

export type Residency = '' | 'own_home' | 'renting' | 'with_family' | 'retirement_village' | 'aged_care' | 'granny_flat';
export type Person = 'client' | 'partner';

/** Plain words for each arrangement, for every screen that names one. */
export const RESIDENCY_WORDS: Record<Exclude<Residency, ''>, string> = {
    own_home: 'in their own home',
    renting: 'renting',
    with_family: 'living with family',
    retirement_village: 'in a retirement village',
    aged_care: 'in residential aged care',
    granny_flat: 'in a granny flat',
};

const num = (v: unknown): number => {
    const x = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(x) ? x : 0;
};

export const isCouple = (ff: FactFind): boolean => ff?.adviceFor === 'couple';

/**
 * The arrangement a person is in for the scenario being modelled: a strategy override first (the
 * aged care modeller moves people into care), then what the profile recorded. A "living with
 * family" answer with a paid-for right to live there reads as a granny flat.
 */
export function residencyOf(ff: FactFind, who: Person, strategyOptions: StrategyOptions = {}): Residency {
    const override = who === 'client' ? strategyOptions?.clientResidencyOverride : strategyOptions?.partnerResidencyOverride;
    const recorded = who === 'client' ? ff?.client?.currentResidency : ff?.partner?.currentResidency;
    const r = (override || recorded || '') as Residency;
    if (r === 'with_family' && isGrannyFlatResidency(r, ff)) return 'granny_flat';
    return r;
}

/** Does anyone in the household live in a home they own — the screens' "own your home" test. */
export function anyoneOwnsHome(ff: FactFind): boolean {
    if (ff?.client?.currentResidency === 'own_home') return true;
    return isCouple(ff) && ff?.partner?.currentResidency === 'own_home';
}

/** Someone is in residential care, under the scenario. */
export function someoneInCare(ff: FactFind, strategyOptions: StrategyOptions = {}): boolean {
    if (residencyOf(ff, 'client', strategyOptions) === 'aged_care') return true;
    return isCouple(ff) && residencyOf(ff, 'partner', strategyOptions) === 'aged_care';
}

/**
 * Could the family home be let out under a plan? Only where there IS one: a home with a value that
 * someone lives in, or the former home of someone now in care. A renter, a village resident or a
 * person living with family has no home to let, and a candidate that lets one anyway models rent
 * from a house that does not exist.
 */
export function homeCanBeLet(ff: FactFind, strategyOptions: StrategyOptions = {}): boolean {
    if (num(ff?.home?.value) <= 0) return false;
    return anyoneOwnsHome(ff) || someoneInCare(ff, strategyOptions);
}

/**
 * A home value recorded for a household in which nobody lives in an owned home and nobody is in
 * care: either a home still owned but not lived in (then it is an asset, and it is counted), or a
 * stale figure left behind when the arrangement changed. The profile says which it is.
 */
export function holdsHomeNobodyLivesIn(ff: FactFind): boolean {
    return num(ff?.home?.value) > 0 && !anyoneOwnsHome(ff) && !someoneInCare(ff);
}

export interface PensionHomeownerAssessment {
    /** Homeowner for the Age Pension assets test (the lower limits), under the scenario. */
    isHomeowner: boolean;
    /** Someone lives in a retirement village under the scenario (Rent Assistance, village fees). */
    rvResident: boolean;
    /**
     * Someone HOLDS a village interest — the profile says so — whether or not the scenario has moved
     * them into care. The entry contribution stays theirs (and assessable at or below the extra
     * allowable amount) until the village refunds it, so this, not `rvResident`, decides whether it
     * is counted. Before 7 Oct 2026 a village resident modelled into care had it drop out entirely.
     */
    rvInterestHeld: boolean;
    rvIsHomeowner: boolean;
    rvEntryContribution: number;
    grannyFlatResident: boolean;
    grannyFlatIsHomeowner: boolean;
    /** What was paid for the granny flat interest (money, property transferred, construction). */
    grannyFlatEntryContribution: number;
}

/**
 * Homeowner status for the Age Pension, in the order Centrelink's rules apply (SSA s 11A):
 *   1. anyone living in an owned home;
 *   2. not if the plan lets the home out or sells it — unless it buys another;
 *   3. a former home kept while in care keeps the owner a homeowner (two years, or while a partner
 *      lives there) — until the projection says that window has closed;
 *   4. a village or granny flat interest above the extra allowable amount makes a homeowner.
 *
 * Moved here from the financials assessor unchanged (7 Oct 2026) so the Support at Home
 * contribution engine, which used to read the client's arrangement alone, applies the same rule.
 */
export function assessPensionHomeowner(ff: FactFind, strategyOptions: StrategyOptions = {}): PensionHomeownerAssessment {
    const partnered = isCouple(ff);
    const clientResidency = residencyOf(ff, 'client', strategyOptions);
    const partnerResidency = partnered ? residencyOf(ff, 'partner', strategyOptions) : '';
    let isHomeowner = clientResidency === 'own_home' || (partnered && partnerResidency === 'own_home');

    if (strategyOptions.rentHome || strategyOptions.downsizeHome) isHomeowner = false;
    if (strategyOptions.downsizeDetails && num(strategyOptions.downsizeDetails.newHomePurchasePrice) > 0) isHomeowner = true;

    const inCare = clientResidency === 'aged_care' || (partnered && partnerResidency === 'aged_care');
    if (inCare && num(ff?.home?.value) > 0 && !strategyOptions.rentHome && !strategyOptions.downsizeHome) isHomeowner = true;
    if (inCare && strategyOptions.formerHomeExemptionEnded) isHomeowner = false;

    const rvResident = clientResidency === 'retirement_village' || (partnered && partnerResidency === 'retirement_village');
    // Once the village has paid the exit entitlement out (8 Oct 2026), the interest is money in hand.
    const rvInterestHeld = !strategyOptions.villageInterestSold && (rvResident
        || ff?.client?.currentResidency === 'retirement_village'
        || (partnered && ff?.partner?.currentResidency === 'retirement_village'));
    const rvEntryContribution = num(ff?.retirementVillage?.entryContribution);
    const rvIsHomeowner = rvInterestHeld && rvEntryContribution > EXTRA_ALLOWABLE_AMOUNT;
    if (rvIsHomeowner) isHomeowner = true;

    const grannyFlatResident = isGrannyFlatResidency(clientResidency, ff) || (partnered && isGrannyFlatResidency(partnerResidency, ff));
    const grannyFlatEntryContribution = num(ff?.grannyFlat?.amountPaid) + num(ff?.grannyFlat?.propertyTransferredValue) + num(ff?.grannyFlat?.constructionCost);
    const grannyFlatIsHomeowner = grannyFlatResident && grannyFlatEntryContribution > EXTRA_ALLOWABLE_AMOUNT;
    if (grannyFlatIsHomeowner) isHomeowner = true;

    return { isHomeowner, rvResident, rvInterestHeld, rvIsHomeowner, rvEntryContribution, grannyFlatResident, grannyFlatIsHomeowner, grannyFlatEntryContribution };
}

/** One person's position, for the screens and the documents. */
export interface PersonPosition {
    who: Person;
    name: string;
    residency: Residency;
    /** "in their own home", "renting", … or '' when the profile has not answered. */
    words: string;
    inCare: boolean;
}

export interface HouseholdPosition {
    client: PersonPosition;
    partner: PersonPosition | null;
    couple: boolean;
    /** The two live in different kinds of arrangement today. */
    livingApart: boolean;
    anyoneOwnsHome: boolean;
    homeValue: number;
    holdsHomeNobodyLivesIn: boolean;
    someoneInCare: boolean;
    bothInCare: boolean;
}

/** The household as the profile records it (no scenario laid over it). */
export function householdPosition(ff: FactFind): HouseholdPosition {
    const couple = isCouple(ff);
    const person = (who: Person): PersonPosition => {
        const residency = residencyOf(ff, who);
        const p = who === 'client' ? ff?.client : ff?.partner;
        return {
            who,
            name: String(p?.firstName || '').trim() || (who === 'client' ? 'the client' : 'their partner'),
            residency,
            words: residency ? RESIDENCY_WORDS[residency] : '',
            inCare: residency === 'aged_care',
        };
    };
    const client = person('client');
    const partner = couple ? person('partner') : null;
    return {
        client,
        partner,
        couple,
        livingApart: !!partner && !!client.residency && !!partner.residency && client.residency !== partner.residency,
        anyoneOwnsHome: anyoneOwnsHome(ff),
        homeValue: num(ff?.home?.value),
        holdsHomeNobodyLivesIn: holdsHomeNobodyLivesIn(ff),
        someoneInCare: client.inCare || !!partner?.inCare,
        bothInCare: !!partner && client.inCare && partner.inCare,
    };
}
